import { Button, CardTag, Css, ModalBody, ModalFooter, ModalHeader, SelectCardGroup, useModal } from "@homebound/beam";
import { useMemo, useState } from "react";
import { SearchBox } from "src/components";
import { MaterialType, Order } from "src/generated/graphqlTypes";
import { queryResult } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";
import { SwapProductFormSwap } from "../swapProductWorkflow";
import {
  ChooseReplacementPossibleMaterials_MaterialVariantFragment,
  ChooseReplacementProjectItemDetailsQuery,
  useChooseReplacementPossibleMaterialsQuery,
  useChooseReplacementProjectItemDetailsQuery,
} from "./ChooseReplacementModal.generated";

type ChooseReplacementModalProps = {
  /** The original PI being replaced; its placeholder requirements drive the search. */
  projectItemId: string;
  onSelect: (replacement: SwapProductFormSwap) => void;
  initialSelectedMaterialVariantId?: string;
};

export function ChooseReplacementModal(props: ChooseReplacementModalProps) {
  const { projectItemId, onSelect, initialSelectedMaterialVariantId } = props;
  const query = useChooseReplacementProjectItemDetailsQuery({ variables: { projectItemId } });
  return queryResult(query, ({ projectItem }) => (
    <ChooseReplacementModalView
      onSelect={onSelect}
      projectItem={projectItem}
      initialSelectedMaterialVariantId={initialSelectedMaterialVariantId}
    />
  ));
}

type ChooseReplacementModalViewProps = Pick<
  ChooseReplacementModalProps,
  "onSelect" | "initialSelectedMaterialVariantId"
> & { projectItem: ChooseReplacementProjectItemDetailsQuery["projectItem"] };

function ChooseReplacementModalView(props: ChooseReplacementModalViewProps) {
  const { projectItem, onSelect, initialSelectedMaterialVariantId = null } = props;
  const { closeModal } = useModal();
  const [search, setSearch] = useState("");
  const [selectedMaterialVariantId, setSelectedMaterialVariantId] = useState<string | null>(
    initialSelectedMaterialVariantId,
  );

  const query = useChooseReplacementPossibleMaterialsQuery({
    variables: {
      projectItemId: projectItem.id,
      filter: { type: [MaterialType.Product], itemId: [projectItem.item.id], search },
      orderBy: { lastUsedAt: Order.Desc },
    },
  });
  return (
    <>
      <ModalHeader>Choose the Replacement Product</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.$}>
          <div css={Css.df.$}>
            <SearchBox onSearch={setSearch} updateQueryString={false} />
          </div>
          {queryResult(query, ({ materialVariants }) => (
            <ReplacementList
              candidates={materialVariants.entities.filter((mv) => mv.id !== projectItem.materialVariant?.id)}
              selectedMaterialVariantId={selectedMaterialVariantId}
              onChange={setSelectedMaterialVariantId}
            />
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="quaternary" onClick={() => closeModal()} />
        <Button
          label="Select"
          disabled={!selectedMaterialVariantId}
          onClick={() => {
            // Flag it as coming from the catalog, so the replacement card re-opens this modal
            // rather than the "define a product" form.
            onSelect({
              proposedMaterialVariantId: selectedMaterialVariantId!,
              proposedMaterialVariantWasCreated: false,
            });
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}

type ReplacementListProps = {
  candidates: ChooseReplacementPossibleMaterials_MaterialVariantFragment[];
  selectedMaterialVariantId: string | null;
  onChange: (materialVariantId: string | null) => void;
};

function ReplacementList(props: ReplacementListProps) {
  const { candidates, selectedMaterialVariantId, onChange } = props;
  const options = useMemo(
    () =>
      candidates.map((mv) => ({
        value: mv.id,
        label: mv.listing.brand?.name ?? mv.listing.name ?? "",
        description: mv.displayName,
        image: mv.featuredImage?.asset?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL,
        ...replacementTag(mv),
      })),
    [candidates],
  );

  if (candidates.isEmpty) {
    return (
      <div css={Css.sm.gray700.$} data-testid="noResults">
        No matching products found.
      </div>
    );
  }

  return (
    <SelectCardGroup
      label="Replacement Products"
      options={options}
      value={selectedMaterialVariantId}
      onChange={onChange}
      layout="horizontal"
      view="grid"
    />
  );
}

function replacementTag(
  mv: ChooseReplacementPossibleMaterials_MaterialVariantFragment,
): { tag: CardTag; tooltip?: string } | undefined {
  if (mv.matchesPlaceholder && mv.hasAwardedCost) {
    return { tag: { text: "READY TO SWAP", type: "success" } };
  } else if (mv.matchesPlaceholder && !mv.hasAwardedCost) {
    return { tag: { text: "NO COST", type: "error" }, tooltip: "There is no cost for this product in this market." };
  } else {
    return { tag: { text: "OTHER SLOT", type: "error" } };
  }
}
